import { SectionIdentifier } from './../../../+shared/components/review-section/review-section.domain';
import { Component, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { LookupOptionsData } from '../../jurisdiction.domain';
import { AllDropdownKeys } from '../../jurisdiction.constants';
import { CommonService } from '../../../services/common.service';
import { distinctUntilChanged } from 'rxjs/operators';
import { FacilitiesFormService } from '../../facilities-form/facilities-form.service';
import { Subscription } from 'rxjs';
import { FiringSystemDataObject } from '../../facilities-form/facilities-form.domain';
import { hideButtonsForEmployeeWhenHasChimneySweeper } from './../../../+shared/components/review-section/review-section.component';
import { DestroyableComponent } from 'src/app/common/destroyable.component';

@Component({
  selector: 'app-exhaust-system-data',
  templateUrl: './exhaust-system-data.component.html',
  styleUrls: ['./exhaust-system-data.component.scss'],
})
export class ExhaustSystemDataComponent extends DestroyableComponent implements OnInit {
  @Input() firingSystemData: FiringSystemDataObject;
  @Input() isReadOnly: boolean = false;
  @Input() applicationId: number | string;
  @Input() isDetailsView: boolean;

  exhaustSystemFG: FormGroup;
  exhaustCheckboxLabels: Array<{ label: string; controlName: string }> = [];
  tableData: Array<any>;
  outerShellThickness: string;
  pipeUnit: string;
  assessmentBase: string;
  outerShellThicknessOptions: LookupOptionsData[];
  pipeUnitOptions: LookupOptionsData[];
  assessmentBaseOptions: LookupOptionsData[];
  formSubscription: Subscription;
  sectionIdentifier = SectionIdentifier;
  hideButtonsForEmployeeWhenHasChimneySweeper = hideButtonsForEmployeeWhenHasChimneySweeper;

  constructor(private commonService: CommonService, private facilitiesFormService: FacilitiesFormService) {
    super();
    const colData = [{ key: 'buildingMaterial' }, { key: 'thicknessInCm' }, { key: 'producerType' }, { key: 'proofOfUsability' }];
    this.tableData = [
      { key: 'innerShell', value: colData },
      { key: 'insulatingMaterial', value: colData },
      { key: 'ringGap', value: colData },
      { key: 'shaft', value: colData },
    ];
  }

  ngOnInit(): void {
    this.addLabelFields();
    this.prepareTableTextFormGroups();
    this.prepareFormControls();
    this.formSubscription = this.preventLeak(
      this.exhaustSystemFG.valueChanges.pipe(distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)))
    ).subscribe(x => {
      if (x.systemUsabilityCheckbox) {
        this.setFormControlValidators('systemUsabilityTextValue');
      } else {
        this.clearFormControlValidatorsAndValue('systemUsabilityTextValue');
      }
      if (x.dimension === 'din') {
        this.setFormControlValidators('din');
      } else {
        this.clearFormControlValidatorsAndValue('din');
      }
      this.facilitiesFormService.registerForComponentFormUpdate.next({
        component: 'exhaustSystem',
        formData: this.exhaustSystemFG,
      });
    });
    this.facilitiesFormService.registerForComponentFormUpdate.next({
      component: 'exhaustSystem',
      formData: this.exhaustSystemFG,
    });
    this.facilitiesFormService.registerForActionOnFormSubmit.subscribe((value: boolean) => {
      if (value) {
        this.resetData();
      }
    });
    this.outerShellThickness = AllDropdownKeys.OuterShellThickness;
    this.pipeUnit = AllDropdownKeys.PipeUnit;
    this.assessmentBase = AllDropdownKeys.AssessmentBase;
    this.outerShellThicknessOptions = this.commonService.getDropdownData(this.outerShellThickness);
    this.pipeUnitOptions = this.commonService.getDropdownData(this.pipeUnit);
    this.assessmentBaseOptions = this.commonService.getDropdownData(this.assessmentBase);
  }

  setFormControlValidators(formControlName: string) {
    this.exhaustSystemFG.get(formControlName).setValidators(Validators.required);
    this.exhaustSystemFG.get(formControlName).markAsTouched();
    this.exhaustSystemFG.get(formControlName).updateValueAndValidity();
  }

  clearFormControlValidatorsAndValue(formControlName: string) {
    this.exhaustSystemFG.get(formControlName).patchValue('', { emitEvent: false });
    this.exhaustSystemFG.get(formControlName).clearValidators();
    this.exhaustSystemFG.get(formControlName).updateValueAndValidity();
  }

  addLabelFields() {
    const labelsControlNames = [
      'chimney',
      'emissionGasLine',
      'airEmissionSystem',
      'systemUsability',
      'singleOccupancy',
      'multipleOccupancy',
      'vacuumOperation',
      'overPressureOperation',
      'moistureSensitive',
      'notMoistureSensitive',
      'assemblyLine',
    ];
    for (let i = 0; i <= labelsControlNames.length - 1; i++) {
      const data = { label: 'label-' + (i + 1), controlName: labelsControlNames[i] + 'Checkbox' };
      this.exhaustCheckboxLabels.push(data);
    }
  }

  prepareTableTextFormGroups() {
    const formGroup = {};
    this.tableData.forEach(x => {
      const form = {};
      x.value.forEach(y => {
        form[y.key] = new FormControl((this.firingSystemData && this.firingSystemData[x.key][y.key]) || '');
      });
      formGroup[x.key] = new FormGroup(form);
      this.exhaustSystemFG = new FormGroup(formGroup);
    });
  }

  prepareFormControls() {
    this.prepareCheckboxesFormControls();
    this.exhaustSystemFG.addControl('systemUsabilityTextValue', new FormControl(this.firingSystemData?.systemUsabilityTextValue || ''));
    this.exhaustSystemFG.addControl('effectiveHeight', new FormControl(this.firingSystemData?.effectiveHeight || ''));
    this.exhaustSystemFG.addControl('chimney', new FormControl(this.firingSystemData?.chimney || ''));
    this.exhaustSystemFG.addControl('fireplace', new FormControl(this.firingSystemData?.fireplace || ''));
    this.exhaustSystemFG.addControl(
      'dimension',
      new FormControl({ value: this.firingSystemData?.dimension || '', disabled: this.isReadOnly })
    );
    this.exhaustSystemFG.addControl('din', new FormControl(this.firingSystemData?.din || ''));
  }

  prepareCheckboxesFormControls() {
    this.exhaustCheckboxLabels.forEach(labelsData => {
      this.exhaustSystemFG.addControl(
        labelsData.controlName,
        new FormControl((this.firingSystemData && this.firingSystemData[labelsData.controlName]) || '')
      );
    });
  }

  getCheckBoxLabel(label: string) {
    return `facilities-data.form-fields.exhaust-system-labels.${label}`;
  }

  getTableLabel(label: string) {
    return `facilities-data.form-fields.${label}`;
  }

  getDimensionRadioLabels(label: string) {
    return `lookup.sk_assessment_base.options.${label}.name`;
  }

  private resetData(): void {
    this.formSubscription.unsubscribe();
  }
}
